import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  alpha,
  ClickAwayListener
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

/* ─── Helpers ─── */
const getSubscriptionStatus = (endDate) => {
  if (!endDate) return { level: null, daysLeft: Infinity };
  const now = new Date();
  const end = new Date(endDate);
  const diffMs = end.getTime() - now.getTime();
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (daysLeft <= 0) return { level: 'expired', daysLeft };
  if (daysLeft <= 3) return { level: 'urgent', daysLeft };
  if (daysLeft <= 7) return { level: 'warning', daysLeft };
  return { level: null, daysLeft };
};

const alertConfig = {
  expired: {
    color: '#ff4655',
    bg: 'rgba(255, 70, 85, 0.10)',
    border: '#ff4655',
    icon: ErrorOutlineIcon
  },
  urgent: {
    color: '#f5a623',
    bg: 'rgba(245, 166, 35, 0.10)',
    border: '#f5a623',
    icon: WarningAmberIcon
  },
  warning: {
    color: '#0ac8b9',
    bg: 'rgba(10, 200, 185, 0.10)',
    border: '#0ac8b9',
    icon: InfoOutlinedIcon
  }
};

/* ─── Component ─── */
const SubscriptionAlert = () => {
  const { t } = useTranslation();
  const profile = useSelector((x) => x.user?.profile);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const endDate = profile?.company?.subscription?.endDate;
  const planName =
    profile?.company?.subscription?.plan?.name ||
    profile?.company?.subscription?.planName ||
    '';
  const { level, daysLeft } = useMemo(
    () => getSubscriptionStatus(endDate),
    [endDate]
  );

  if (!level) return null;

  const cfg = alertConfig[level];
  const Icon = cfg.icon;

  const getMessage = () => {
    switch (level) {
      case 'expired':
        return t('subscription_expired', 'Your subscription has expired!');
      case 'urgent':
        return t('subscription_urgent', {
          defaultValue: 'Subscription expiring in {{days}} days',
          days: daysLeft
        });
      case 'warning':
        return t('subscription_warning', {
          defaultValue: 'Subscription renews in {{days}} days',
          days: daysLeft
        });
      default:
        return '';
    }
  };

  const formattedEndDate = endDate
    ? new Date(endDate).toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : '—';

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: cfg.color, display: 'block', mb: 0.5 }}
      >
        {getMessage()}
      </Typography>
      {planName && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'rgba(255,255,255,0.7)' }}
        >
          {t('Plan', 'Plan')}:{' '}
          <strong style={{ color: '#fff' }}>{planName}</strong>
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{ display: 'block', color: 'rgba(255,255,255,0.7)' }}
      >
        {t('End Date', 'End Date')}:{' '}
        <strong style={{ color: cfg.color }}>{formattedEndDate}</strong>
      </Typography>
      {daysLeft > 0 && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'rgba(255,255,255,0.7)' }}
        >
          {t('Time left', 'Time left')}:{' '}
          <strong style={{ color: cfg.color }}>
            {daysLeft} {t('days', 'days')}
          </strong>
        </Typography>
      )}
    </Box>
  );

  const handleToggle = () => {
    setTooltipOpen((prev) => !prev);
  };

  const handleClose = () => {
    setTooltipOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        <Tooltip
          arrow
          title={tooltipContent}
          placement="bottom"
          open={tooltipOpen}
          onOpen={() => setTooltipOpen(true)}
          onClose={() => setTooltipOpen(false)}
          disableFocusListener
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#1e1f22',
                border: `1px solid ${alpha(cfg.color, 0.25)}`,
                borderRadius: '8px',
                boxShadow: `0 8px 24px rgba(0,0,0,0.4), 0 0 12px ${alpha(
                  cfg.color,
                  0.1
                )}`,
                maxWidth: 280,
                p: 1
              }
            },
            arrow: {
              sx: { color: '#1e1f22' }
            }
          }}
        >
          <Box
            onClick={handleToggle}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.2,
              py: 0.4,
              borderRadius: '6px',
              backgroundColor: cfg.bg,
              border: `1px solid ${alpha(cfg.border, 0.25)}`,
              flexShrink: 0,
              maxWidth: { xs: 200, sm: 320 },
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              userSelect: 'none',
              '&:hover': {
                backgroundColor: alpha(cfg.border, 0.15),
                borderColor: alpha(cfg.border, 0.4)
              }
            }}
          >
            <Icon
              sx={{
                fontSize: '0.9rem',
                color: cfg.color,
                flexShrink: 0
              }}
            />

            <Typography
              noWrap
              variant="caption"
              sx={{
                color: cfg.color,
                fontWeight: 600,
                fontSize: { xs: '0.62rem', sm: '0.7rem' },
                letterSpacing: '0.2px',
                lineHeight: 1.3,
                flex: 1,
                minWidth: 0
              }}
            >
              {getMessage()}
            </Typography>
          </Box>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
};

export default SubscriptionAlert;
